"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { usePathname } from "next/navigation";

import type { IconName } from "@gogive/design-system/icons";

import { findRouteChain } from "@/config/navigation";

export type BreadcrumbOverride = {
  label: string;
  iconName?: IconName;
};

export type BreadcrumbResolver = () => BreadcrumbOverride | null | undefined;

type BreadcrumbRegistryContextValue = {
  overrides: Readonly<Record<string, BreadcrumbOverride>>;
  setOverride: (routePath: string, value: BreadcrumbOverride) => void;
  clearOverride: (routePath: string) => void;
};

const BreadcrumbRegistryContext =
  createContext<BreadcrumbRegistryContextValue | null>(null);

function useBreadcrumbRegistryContext(): BreadcrumbRegistryContextValue {
  const ctx = useContext(BreadcrumbRegistryContext);
  if (!ctx) {
    throw new Error(
      "Breadcrumb registry hooks must be used within BreadcrumbRegistryProvider",
    );
  }
  return ctx;
}

export function BreadcrumbRegistryProvider({ children }: { children: ReactNode }) {
  const [overrides, setOverrides] = useState<
    Record<string, BreadcrumbOverride>
  >({});

  const setOverride = useCallback(
    (routePath: string, value: BreadcrumbOverride) => {
      setOverrides((prev) => {
        const current = prev[routePath];
        if (
          current?.label === value.label &&
          current?.iconName === value.iconName
        ) {
          return prev;
        }
        return { ...prev, [routePath]: value };
      });
    },
    [],
  );

  const clearOverride = useCallback((routePath: string) => {
    setOverrides((prev) => {
      if (!(routePath in prev)) return prev;
      const next = { ...prev };
      delete next[routePath];
      return next;
    });
  }, []);

  const value = useMemo(
    () => ({ overrides, setOverride, clearOverride }),
    [overrides, setOverride, clearOverride],
  );

  return (
    <BreadcrumbRegistryContext.Provider value={value}>
      {children}
    </BreadcrumbRegistryContext.Provider>
  );
}

/** Read registered overrides (used by the top bar breadcrumb hook). */
export function useBreadcrumbOverrides(): Readonly<
  Record<string, BreadcrumbOverride>
> {
  return useBreadcrumbRegistryContext().overrides;
}

/**
 * Register a dynamic breadcrumb label for a nav route pattern
 * (e.g. `/donors/[donorId]`). Cleared on unmount.
 */
export function useRegisterBreadcrumb(
  routePath: string,
  label: string | null | undefined,
  options?: { iconName?: IconName },
): void {
  const { setOverride, clearOverride } = useBreadcrumbRegistryContext();
  const iconName = options?.iconName;

  useEffect(() => {
    if (!routePath) return undefined;

    const trimmed = label?.trim();
    if (trimmed) {
      setOverride(routePath, {
        label: trimmed,
        ...(iconName ? { iconName } : {}),
      });
    } else {
      clearOverride(routePath);
    }

    return () => clearOverride(routePath);
  }, [routePath, label, iconName, setOverride, clearOverride]);
}

/**
 * Register a breadcrumb resolver for a nav route pattern. Re-registers when the
 * resolved label or icon changes.
 */
export function useRegisterBreadcrumbResolver(
  routePath: string,
  resolver: BreadcrumbResolver,
): void {
  const resolved = resolver();
  useRegisterBreadcrumb(routePath, resolved?.label ?? null, {
    iconName: resolved?.iconName,
  });
}

/** Register a resolver for the leaf route of the current pathname. */
export function useRegisterPageBreadcrumbResolver(
  resolver: BreadcrumbResolver,
): void {
  const pathname = usePathname() ?? "/";
  const routePath = useMemo(() => {
    const chain = findRouteChain(pathname);
    return chain[chain.length - 1]?.path ?? "";
  }, [pathname]);

  useRegisterBreadcrumbResolver(routePath, resolver);
}
